import * as Sentry from '@sentry/react';
import isLocalhost from './isLocalHost';

/**
 * Determines the environment based on REACT_APP_SERVER_DOMAIN
 * Matches backend pattern: beacon.production, beacon.dev, etc.
 */
function getEnvironment(): string {
    const serverDomain = (
        process.env.REACT_APP_SERVER_DOMAIN || ''
    ).toLowerCase();

    if (serverDomain.includes('frontdoor.mymedgas.com')) return 'production';
    if (serverDomain.includes('mmg-staging-api')) return 'staging';
    if (serverDomain.includes('mmg-test-api')) return 'test';
    if (serverDomain.includes('mmg-api-dev')) return 'dev';
    if (serverDomain.includes('localhost')) return 'dev';

    // Default to dev
    return 'dev';
}

/**
 * Sentry reporting for the Web app. Deliberately narrow: warnings and errors only.
 * No session tracking, no performance tracing, no breadcrumbs, no automatic
 * instrumentation of clicks/fetch/navigation - the previous setup logged every
 * redux action and flooded the timeline.
 */
class SentryLogger {
    private initialized = false;
    private currentUser: string | null = null;

    init(): void {
        if (this.initialized) return;

        const dsn = process.env.REACT_APP_SENTRY_DSN;
        if (isLocalhost || !dsn) {
            console.info('[Sentry]: Disabled (localhost or missing DSN)');
            return;
        }

        try {
            const environment = getEnvironment();

            Sentry.init({
                dsn,
                environment,
                release: process.env.REACT_APP_VERSION || '0.0.0',
                // defaultIntegrations:false is essential - an integrations array alone is MERGED
                // with the defaults, which include Breadcrumbs (clicks/fetch/history).
                defaultIntegrations: false,
                integrations: [
                    new Sentry.Integrations.GlobalHandlers(),
                    new Sentry.Integrations.TryCatch(),
                    new Sentry.Integrations.Dedupe(),
                    new Sentry.Integrations.FunctionToString(),
                    new Sentry.Integrations.LinkedErrors(),
                    new Sentry.Integrations.HttpContext(),
                    // Required for denyUrls below to take effect at all.
                    new Sentry.Integrations.InboundFilters()
                ],
                // Posts to Sentry on page load and navigation - exactly the flood to avoid.
                autoSessionTracking: false,
                tracesSampleRate: 0,
                maxBreadcrumbs: 0,
                sendClientReports: false,
                // Third-party scripts we don't own.
                denyUrls: [
                    /mixpanel\.com/i,
                    /google-analytics\.com/i,
                    /googletagmanager\.com/i,
                    /doubleclick\.net/i,
                    /facebook\.net/i
                ],
                initialScope: { tags: { subsystem: 'Web' } }
            });

            this.initialized = true;
            console.info(
                `[Sentry]: Initialized - Environment: ${environment}, Subsystem: Web`
            );
        } catch (error) {
            console.error('[Sentry]: Initialization failed', error);
        }
    }

    setUser(email: string): void {
        if (!this.initialized) return;

        try {
            this.currentUser = email;
            Sentry.setUser({ id: email, email });
        } catch (error) {
            console.error('[Sentry]: Failed to set user', error);
        }
    }

    clearUser(): void {
        this.currentUser = null;
        if (!this.initialized) return;

        try {
            Sentry.setUser(null);
        } catch (error) {
            console.error('[Sentry]: Failed to clear user', error);
        }
    }

    /**
     * Report a warning.
     * @param path Component/method path (e.g. "ReduxMiddleware")
     * @param message Human-readable message
     * @param payload Optional context data
     * @param user Optional user email (uses current user if not provided)
     */
    logWarning(
        path: string,
        message: string,
        payload?: any,
        user?: string
    ): void {
        if (!this.initialized) return;

        try {
            Sentry.withScope(scope => {
                this.applyScope(scope, 'warning', path, payload, user);
                Sentry.captureMessage(message, 'warning');
            });
        } catch (error) {
            console.error('[Sentry]: Failed to log warning', error);
        }
    }

    /**
     * Report an error. Real Error objects are captured with their stack so Sentry
     * groups them properly; strings fall back to a message.
     */
    logError(
        path: string,
        error: Error | string,
        payload?: any,
        user?: string
    ): void {
        if (!this.initialized) return;

        try {
            Sentry.withScope(scope => {
                this.applyScope(scope, 'error', path, payload, user);
                if (error instanceof Error) Sentry.captureException(error);
                else Sentry.captureMessage(error, 'error');
            });
        } catch (err) {
            console.error('[Sentry]: Failed to log error', err);
        }
    }

    isEnabled(): boolean {
        return this.initialized;
    }

    private applyScope(
        scope: Sentry.Scope,
        level: 'warning' | 'error',
        path: string,
        payload?: any,
        user?: string
    ): void {
        scope.setLevel(level);
        scope.setTag('path', path);
        scope.setTransactionName(path);

        const email = user || this.currentUser;
        if (email) scope.setUser({ id: email, email });
        if (payload) scope.setExtra('Payload', payload);
    }
}

// Export singleton instance
export default new SentryLogger();
